import React from 'react'
import {
  BlockOutlined,
  CircleOutlined,
  MailOutline,
  PersonPinOutlined,
  PersonPinSharp,
  PhoneOutlined,
  PublicOffOutlined,
  PublicOutlined,
  WorkOutline,
} from '@mui/icons-material'
import { ListItem, ListItemAvatar, ListItemText } from '@mui/material'
import type { Invitation, User } from '@app/src/types/resourceExplorer'
import { useOwnerRestriction } from '@app/src/hooks/useOwnerRestriction'
import { Roles } from '@app/src/wf-constants'
import { useIntl } from 'react-intl'

export interface EmailListItemProps {
  user: User | Invitation
}
export const EmailListItem: React.FC<EmailListItemProps> = ({ user }) => {
  const { formatMessage } = useIntl()
  return (
    <ListItem>
      <ListItemAvatar>
        <MailOutline color="action" />
      </ListItemAvatar>
      <ListItemText primary={formatMessage({ id: 'schemas.user.email' })} secondary={user.email} />
    </ListItem>
  )
}

export interface PositionListItemProps {
  user: User
}
export const PositionListItem: React.FC<PositionListItemProps> = ({ user }) => {
  const { formatMessage } = useIntl()
  return (
    <ListItem>
      <ListItemAvatar>
        <WorkOutline color="action" />
      </ListItemAvatar>
      <ListItemText
        primary={formatMessage({ id: 'schemas.user.position' })}
        secondary={user.position || formatMessage({ id: 'general.notAdded' })}
      />
    </ListItem>
  )
}

export interface PhoneListItemProps {
  user: User
}
export const PhoneListItem: React.FC<PhoneListItemProps> = ({ user }) => {
  const { formatMessage } = useIntl()
  return (
    <ListItem>
      <ListItemAvatar>
        <PhoneOutlined color="action" />
      </ListItemAvatar>
      <ListItemText
        primary={formatMessage({ id: 'schemas.user.phoneNumber' })}
        secondary={user.phoneNumber || formatMessage({ id: 'general.notAdded' })}
      />
    </ListItem>
  )
}

export interface RoleListItemProps {
  user: User | Invitation
}
export const RoleListItem: React.FC<RoleListItemProps> = ({ user }) => {
  const { formatMessage } = useIntl()
  const role = (user.role || '').toLowerCase()
  const isContributor = role === Roles.Contributor
  return (
    <ListItem>
      <ListItemAvatar>
        {isContributor ? <BlockOutlined color="action" /> : <CircleOutlined color="action" />}
      </ListItemAvatar>
      <ListItemText primary={formatMessage({ id: 'schemas.user.role' })} secondary={user.role} />
    </ListItem>
  )
}

export interface PublicContactListItemProps {
  user: User
}
export const PublicContactListItem: React.FC<PublicContactListItemProps> = ({ user }) => {
  const { formatMessage } = useIntl()
  return (
    <ListItem>
      <ListItemAvatar>
        {user.isPublicContact ? <PublicOutlined color="action" /> : <PublicOffOutlined color="action" />}
      </ListItemAvatar>
      <ListItemText
        primary={formatMessage({ id: 'schemas.user.publicContact' })}
        secondary={formatMessage({ id: 'schemas.user.publicWfNetwork' }, { isPublic: user.isPublicContact })}
      />
    </ListItem>
  )
}

export interface OwnerRestrictionListItemProps {
  user: User | Invitation
}
export function OwnerRestrictionListItem({ user }: OwnerRestrictionListItemProps) {
  const { formatMessage } = useIntl()
  const { accountSupportsOwnerRestriction } = useOwnerRestriction()
  const role = (user.role || '').toLowerCase()
  const isAdmin = role === Roles.Admin

  if (!accountSupportsOwnerRestriction) {
    return null
  }

  // don't render if we don't have that data from the API
  if (user.isOwnerRestricted === undefined) {
    return null
  }

  return (
    <ListItem sx={{ opacity: isAdmin ? 0.5 : 1 }}>
      <ListItemAvatar>
        {user.isOwnerRestricted ? <PersonPinSharp color="action" /> : <PersonPinOutlined color="action" />}
      </ListItemAvatar>
      <ListItemText
        primary={formatMessage({ id: 'permissions.ownerRestricted.name' })}
        secondary={
          isAdmin
            ? formatMessage({ id: 'general.notApplicable' })
            : user.isOwnerRestricted
              ? formatMessage({ id: 'general.yes' })
              : formatMessage({ id: 'general.no' })
        }
      />
    </ListItem>
  )
}

export default {
  EmailListItem,
  PositionListItem,
  PhoneListItem,
  RoleListItem,
  PublicContactListItem,
  OwnerRestrictionListItem,
}
